import * as CarouselElements from 'react-aria-carousel';
import styled from 'styled-components';

export const Wrapper = styled.section`
  margin-inline: calc(50% - 50vw);
  padding-inline: calc(50vw - 50%);
  padding-block: var(--spacing-base);
`;

export const Inner = styled.div<{ $noAutoplay?: boolean }>`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 0;

  @media (width < 48rem) {
    padding: 0 ${props => (props.$noAutoplay ? '0' : '32px')} 0 0;
  }
`;

export const Head = styled.div`
  margin-bottom: 24px;

  @media (width < 48rem) {
    margin-bottom: 16px;
  }
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: var(--font-size-text-xl);
  font-weight: var(--font-weight-bold);
  color: var(--black);
  line-height: 32px;
  text-transform: uppercase;

  .surface-moss-green & {
    color: var(--white);
  }

  @media (width < 48rem) {
    font-size: var(--font-size-text-lg);
    line-height: 24px;
    & [data-carousel-item] > * {  width: 100%;
  }
`;

export const Rule = styled.span`
  flex: 1;
  height: 1px;
  background: var(--grey-300);

  .surface-moss-green & {
    background: rgba(255, 255, 255, 0.3);
  }

  @media (width < 48rem) {
    display: none;
  }
`;

export const SubRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-top: 8px;

  @media (width < 48rem) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-top: 8px;
  }
`;

export const Description = styled.div<{ $noAutoplay?: boolean }>`
  margin: 0;
  margin-right: 36px;
  flex: 1 1 auto;
  min-width: 0;
  font-size: 15px;
  line-height: 1.7;

  @media (width < 48rem) {
    margin-right: ${props => (props.$noAutoplay ? '0' : '8px')};
  }
`;

export const SubRowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 20px;
  margin-left: auto;
`;

export const ViewAllLink = styled.a`
  font-size: var(--font-size-text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--brand-900);
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }

  .surface-moss-green & {
    color: var(--white);
  }
`;

export const Carousel = styled(CarouselElements.Carousel)`
  width: 100%;
`;

export const PlayPauseWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 3;
  transform: translateY(calc(-100% + 40px));
  padding-bottom: 8px;

  @media (width < 48rem) {
    top: -40px;
    right: 0;
    transform: none;
    padding-bottom: 0;
  }
`;

export const Track = styled.div`
  /* Arrow is 30px wide at left/right: 0; add 8px clearance */
  --arrow-clearance: calc(30px + 8px);
  position: relative;
  padding-inline: var(--arrow-clearance);

  @media (width < 48rem) {
    padding-inline: 0;
  }
`;

export const Main = styled(CarouselElements.CarouselScroller)`
  --gap: 24px;
  --visible: 5;

  display: grid;
  overflow: auto;
  scroll-snap-type: x mandatory;
  grid-auto-flow: column;
  column-gap: var(--gap);
  grid-auto-columns: calc((100% - (var(--visible) - 1) * var(--gap)) / var(--visible));
  scrollbar-width: none;
  justify-content: safe center !important;
  padding-block-end: 5px;

  &:focus-visible {
    box-shadow: var(--shadow-focus);
    border: 1px solid var(--brand-600);
    outline: 0;
  }

  /* Snap each tile to the start edge so the peek tile scrolls fully into view */
  & > * {
    scroll-snap-align: start;
  }

  @media (width < 48rem) {
    /* Override ProductTile's base max-width: 198px so it fills the grid cell.
       Path: Main (scroller) → CarouselItem → Tile. */
    & > * > * {
      max-width: none;
      width: 100%;
    }

    /* Ensure partially-visible tiles clip within their own box so
       hit-testing targets the tile's link, not the scroll container */
    & > * {
      overflow: hidden;
    }
  }
`;
